<template>
  <CustomForm
      v-slot="{ errors }"
      @submit="onSubmit"
      class="w-full max-w-[524px] m-auto px-5 lg:px-0" ref="formCheckoutRef"
  >
    <div class="w-full pt-10 pb-20 sm:py-[81px]">
      <div class="transition-all duration-300 ease-linear">
        <YourInformation :emailEmptyShowChecker="emailEmptyShowChecker" :errors="errors" :onEmailBlur="handleEmailBlur"/>
        <div class="pt-6 pb-[31px] border-b border-b-light-gray">
          <SharedCheckBox label="Sign up for Vinderkind emails" v-model="renewYear"/>
        </div>
        <DeliverInformation
            :deliveryInfo="deliveryInfo"
            :errors="errors"
            :defaultPostcode="postcode"
            :isCheckout="true"
            @zip-validation-error="handleZipValidationError"
            @zip-validation-success="handleZipValidationSuccess"
        />
        <PaymentInformation
            :errors="errors"
            page="checkout"
        />
        <button
            :disabled="spinning || hasZipValidationError"
            class="mt-[15px] mb-[18px] bg-vivid-purple w-full py-4 text-[21px]/[25px] text-white rounded-small font-bold hover:opacity-90 transition-all ease-in duration-150 disabled:opacity-50"
            :class="{ 'cursor-not-allowed': hasZipValidationError }"
        >
          <span
              v-if="spinning"
              class="px-[15px] cursor-pointer flex items-center bg-vivid-purple rounded-small justify-center w-full text-white gap-[13px] sm:gap-5 hover:opacity-85 transition-all duration-200 ease-linear [&_.loader]:!min-h-5 [&_.loader]:!h-5 [&_.item]:!w-6 [&_.item]:!h-6 [&_.item]:!border-2"
          >
            <CustomLoader/>
          </span>
          <span v-else>
            <template v-if="cartResponse.isFullyDiscounted">Checkout</template>
            <template v-else>Pay ${{ Number(calculateTotal()).toFixed(2) }}</template>
          </span>
        </button>

        <!-- Show validation message near submit button if there's an error -->
        <div v-if="hasZipValidationError && zipValidationMessage" class="text-red-500 text-sm mt-2 mb-2 text-center">
          {{ zipValidationMessage }}
        </div>

        <div class="text-[13px]/[17px] flex justify-center gap-1">
          <span class="text-center">By continuing you agree with our</span>
          <button @click.prevent="openModal" class="text-center text-vivid-purple">
            terms and conditions.
          </button>
        </div>
      </div>
    </div>
  </CustomForm>

  <SharedModal v-model="open">
    <TermsConditions @close="closeModal"/>
  </SharedModal>
</template>

<script lang="ts">
import SharedCheckBox from '@components/reusable/SharedCheckBox.vue'
import YourInformation from '@components/sign-up/YourInformation.vue'
import DeliverInformation from '@components/sign-up/DeliverInformation.vue'
import PaymentInformation from '@components/payments/PaymentInformation.vue'
import CheckoutZipField from '@components/checkout/CheckoutZipField.vue'
import {defineComponent, onMounted, type Ref, ref, useTemplateRef, watch} from 'vue'
import {usePlansStore} from '@stores/plans'
import {Form as CustomForm} from 'vee-validate'
import SharedModal from '@components/reusable/SharedModal.vue'
import TermsConditions from '@components/sign-up/TermsConditions.vue'
import {emailValidation} from '@utills/helpers/validation'
import {useCartsStore} from '@stores/carts'
import CustomLoader from '@components/reusable/CustomLoader.vue'
import {useOrderStore} from '@stores/sign-up'
import {useReCaptcha} from "vue-recaptcha-v3";
import {usePayment} from "@stores/payment";

export default defineComponent({
  components: {
    SharedCheckBox,
    YourInformation,
    DeliverInformation,
    CustomForm,
    PaymentInformation,
    SharedModal,
    TermsConditions,
    CustomLoader,
    CheckoutZipField
  },
  data() {
    return {
      deliveryInfo: [
        {
          placeholder: 'Name',
          field: 'delivery_name',
          valid: this.required,
        },
        {
          placeholder: 'Address',
          field: 'delivery_line_one',
          valid: this.required,
        },
        {
          placeholder: 'Address Line 2',
          field: 'delivery_line_two',
          valid: () => true
        },
        {
          placeholder: 'City',
          field: 'delivery_city',
          valid: this.required,
        },
        {
          placeholder: 'State',
          field: 'delivery_state',
          valid: this.required,
        },
        {
          placeholder: 'ZIP Code',
          field: 'delivery_zip',
          valid: this.validateUSZip,
          handler: true
        }
      ]
    }
  },

  setup() {
    const formCheckoutRef = useTemplateRef('formCheckoutRef') as Ref<HTMLFormElement | null>;
    const emailEmptyShowChecker = ref(false)
    const {getPaymentData} = usePayment()
    const signUp = useOrderStore()
    const reCaptcha = useReCaptcha();
    const {carts, calculateTotal, cartResponse, postcode, setCustomerEmail} = useCartsStore()
    let zipCode = ref('')
    let spinning = ref(false)
    const {publication} = usePlansStore()
    let choosePlan = ref(1)
    let validZips = ['00000']
    let renewYear = ref(false)
    const couponCode = ref('')
    const hasZipValidationError = ref(false)
    const zipValidationMessage = ref('')

    function required(value: string) {
      return value ? true : 'This field is required'
    }

    function validateUSZip(value: string) {
      if (!value) return 'This field is required'

      // Only allow US ZIP codes (5 digits)
      if (!/^\d{5}$/.test(value.trim())) {
        return 'Please enter a valid US ZIP code (5 digits)'
      }

      return true
    }

    watch(cartResponse, () => {
      couponCode.value = cartResponse.couponCode;
    })

    async function onSubmit(values: any) {
      spinning.value = true

      // Check for ZIP validation errors before proceeding
      if (hasZipValidationError.value) {
        spinning.value = false;

        // Scroll to the ZIP field to show the error
        document.querySelector(`[name="delivery_zip"]`)?.scrollIntoView({ behavior: "smooth" });
        return;
      }

      if(!values['customer_email'] && !values['customer_empty_email']){
        spinning.value = false;
        emailEmptyShowChecker.value = true;

        if(formCheckoutRef.value) {
          formCheckoutRef.value.setErrors({
            'customer_email': 'This field is required'
          })
        }

        document.querySelector(`[name="customer_email"]`)?.scrollIntoView({ behavior: "smooth" });

        return;
      }

      const tokenRecaptcha = async () => {
        await reCaptcha?.recaptchaLoaded();
        return reCaptcha?.executeRecaptcha('login');
      }


      let customer = {
        name: values.customer_name,
        email: null,
        phone: values.customer_phone,
        has_email: false,
        // signUp: signup
      }

      if(!values['customer_empty_email']){
        customer.email = values['customer_email']
        customer.has_email = true
      }

      const address = {
        city: values.delivery_city,
        name: values.delivery_name,
        state: values.delivery_state,
        zip: values.delivery_zip,
        address_line_1: values.delivery_line_one,
        address_line_2: values.delivery_line_two,
      }

      if(cartResponse.isFullyDiscounted){
        const data = {
          paymentInfo: [],
          customer,
          products: carts,
          address,
        };

        await signUp.submitOrder(data, '/order-confirmation', await tokenRecaptcha())
        spinning.value = false
        return;
      }

      const paymentInfo = await getPaymentData()

      if(paymentInfo === 'error'){
        spinning.value = false
        return;
      }

      const data = {
        paymentInfo,
        customer,
        products: carts,
        address,
      };

      await signUp.submitOrder(data, '/order-confirmation', await tokenRecaptcha())
      spinning.value = false
    }

    const updateChoosePlan = (planId: number) => {
      choosePlan.value = planId
    }

    const handleZipValidationError = (message: string) => {
      hasZipValidationError.value = true
      zipValidationMessage.value = message
    }

    const handleZipValidationSuccess = () => {
      hasZipValidationError.value = false
      zipValidationMessage.value = ''
    }

	    const handleEmailBlur = async (
	      payload: { email?: string | null; phone?: string | null } | string
	    ) => {
	      try {
	        await setCustomerEmail(payload as any)
	      } catch (error) {
	        console.error('Error setting customer contact info:', error)
	      }
	    }

    const open = ref(false)
    const openModal = () => {
      open.value = true
    }
    const closeModal = () => {
      open.value = false
    }
    return {
      cartResponse,
      postcode,
      validZips,
      publication,
      choosePlan,
      updateChoosePlan,
      renewYear,
      calculateTotal,
      required,
      validateUSZip,
      onSubmit,
      emailValidation,
      zipCode,
      open,
      openModal,
      closeModal,
      spinning,
      couponCode,
      emailEmptyShowChecker,
      hasZipValidationError,
      zipValidationMessage,
      handleZipValidationError,
      handleZipValidationSuccess,
      handleEmailBlur
    }
  }
})
</script>
